import { CountdownTimer, MUIIcon, ProgressIndicator, useCalcRemainingTime } from "../../../../widgets";
import { FC, useCallback, useState, useEffect } from "react";
import { IServiceRequest, ServiceRequestStatus } from "../models";

import { ServiceReqHelper } from "../helpers/helper";
import { ServiceRequestResend } from "./ServiceRequestResend";
import { addMinutes } from "../../timeline/helper";
import { useInterval } from "react-use";
import { useServiceRequestContext } from "../service-request";
import { useGetServiceRequestGatePassId } from "../helpers";
import { useGetContactDetailsQuery, useLazyGetContactDetailsQuery } from "../../../../services/data/table/contactsApi";
import { useAuth } from "../../../../../modules/auth";
import { useGetCancelTimer } from "../helpers/useGetCancelTimer";

interface IServiceRequestActionsProps {
    isSendingForApproval?: boolean,
    onCancel: (requestId: string) => void
    onReOpen: (requestId: string) => void
    onExtended: (requestId: string, extendGPId: string) => void
}

export const ServiceRequestActions: FC<IServiceRequestActionsProps> = ({ isSendingForApproval, onCancel, onReOpen, onExtended }) => {
    const { result, refetch, updateServiceRequest, updateExtendServiceRequest } = useServiceRequestContext()
    const { serviceRequest, service } = result || {}
    const { gpOut, gpIn } = useGetServiceRequestGatePassId(serviceRequest)
    const endTime = addMinutes(serviceRequest?.endTime, service?.otpTimer)
    const { isFuture } = useCalcRemainingTime({ endTime });
    const [cancelling, setCancelling] = useState<boolean>(false)
    const [reopening, setReopening] = useState<boolean>(false);
    const [extend, setExtend] = useState<boolean>(false);
    const [getContactDetails, { data: contactDetails, isLoading }] = useLazyGetContactDetailsQuery()
    const { cancelTimer } = useGetCancelTimer();
    const isTimerBasedCancel =
        serviceRequest?.statusCode === ServiceRequestStatus.AutoApproved ||
        serviceRequest?.statusCode === ServiceRequestStatus.Approved;
    const EXTRA_SECONDS = 3;
    const cancelEndTime =
        serviceRequest?.cancelTime && cancelTimer !== null && cancelTimer !== undefined
            ? new Date(new Date(serviceRequest.cancelTime).getTime() + (cancelTimer + EXTRA_SECONDS) * 1000)
            : undefined;
    const { duration: cancelDuration, isFuture: isCancelFuture } =
        useCalcRemainingTime({ endTime: cancelEndTime });
    const totalCancelSeconds = (cancelDuration?.minutes || 0) * 60 + (cancelDuration?.seconds || 0);
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [cancelAttempted, setCancelAttempted] = useState(false);
    const [showOutPopup, setShowOutPopup] = useState(false);
    const [prevGpOut, setPrevGpOut] = useState<boolean | undefined>(gpOut);
    const {user} = useAuth();
    
    useEffect(() => {
        const wasCancelRunning =
            cancelEndTime && isCancelFuture !== false;

        if (prevGpOut === false && gpOut === true && wasCancelRunning) {
            setShowOutPopup(true);
        }

        setPrevGpOut(gpOut);

    }, [gpOut, cancelEndTime, isCancelFuture]);

    useEffect(() => {
        if (!showOutPopup) return;

        const timer = setTimeout(() => {
            setShowOutPopup(false);
        }, 10000);

        return () => clearTimeout(timer);

    }, [showOutPopup]);

    useEffect(() => {
        if (!user) return;
        getContactDetails({ contactId: user.contactId || '' })

        // eslint-disable-next-line
    }, [getContactDetails])

    useInterval(() => {
        if (serviceRequest?.statusCode !== ServiceRequestStatus.Submitted && serviceRequest?.statusCode !== ServiceRequestStatus.AutoApproved && serviceRequest?.statusCode !== ServiceRequestStatus.Approved)
            return;

        refetch()
    }, 5000)

    const resendButtons = (serviceRequest: IServiceRequest) => {
        if (!ServiceReqHelper.isCancellable(serviceRequest?.statusCode) || !serviceRequest.endTime || isFuture !== false)
            return <></>

        if ((service?.maxApprovalAttempts && serviceRequest.counter) &&
            service.maxApprovalAttempts <= serviceRequest.counter) {
            return <></>
        }

        return <>
            <ServiceRequestResend serviceRequest={serviceRequest} />
        </>
    }

    const countdownTimer = useCallback((endTime?: Date) => {
        if (!endTime || !service?.otpTimer || service?.otpTimer <= 0 || isFuture !== true ||
            !ServiceReqHelper.isCancellable(serviceRequest?.statusCode))
            return <></>

        return <>{
            serviceRequest?.statusCode === ServiceRequestStatus.Submitted &&
            <CountdownTimer endTime={endTime} label='Waiting for Approval' />
        }
        </>

        // eslint-disable-next-line
    }, [isFuture, service, serviceRequest])

    const handleCancel = () => {
        if (!serviceRequest) return;

        setCancelling(true);
        if (isTimerBasedCancel) {
            updateServiceRequest({
                ...serviceRequest,
                cancelTime: new Date(),
            })
                .then(() => refetch())
                .finally(() => setCancelling(false));

            return;
        }
        updateServiceRequest({
            ...serviceRequest,
            statusCode: ServiceRequestStatus.Cancelled
        })
            .then(() => onCancel(serviceRequest.id))
            .finally(() => setCancelling(false));
    };

    useEffect(() => {
        if (!isTimerBasedCancel) return;
        if (!cancelEndTime || !serviceRequest) return;
        if (cancelAttempted || cancelError) return;
        if (gpOut === true) return;
        const now = new Date();
        if (now < cancelEndTime) return;

        const requestId = serviceRequest.id;

        setCancelling(true);
        setCancelAttempted(true);

        updateServiceRequest({
            ...serviceRequest,
            statusCode: ServiceRequestStatus.Cancelled
        })
            .then(() => {
                setCancelError(null);
                onCancel(requestId);
            })
            .catch((err) => {
                let message = "Error occurred";

                if (err?.data?.message) {
                    message = err.data.message;
                } else if (err?.response?.data?.message) {
                    message = err.response.data.message;
                } else if (err?.message) {
                    message = err.message;
                } else if (typeof err === "string") {
                    message = err;
                }

                setCancelError(message);
            })
            .finally(() => setCancelling(false));

    }, [cancelEndTime, serviceRequest, isTimerBasedCancel, cancelAttempted, cancelError, gpOut]);

    const cancellationButton = () => {
        if (!ServiceReqHelper.isCancellable(serviceRequest?.statusCode) || serviceRequest?.allowCancellation === false)
            return <></>

        return <>
            <button
                disabled={cancelling || (isTimerBasedCancel && !!cancelEndTime)}
                type="button"
                onClick={handleCancel}
                className='btn btn-sm btn-danger py-2 align-self-center'
            >
                {!cancelling && !isCancelFuture && (
                    <>
                        <MUIIcon iconName={"CancelOutlined"} className="me-1" />
                        Cancel the request
                    </>
                )}

                {isTimerBasedCancel && gpOut !== true && cancelEndTime && isCancelFuture !== false && (
                    <span>
                        Your gate pass cancellation request is under process. Please wait!
                    </span>
                )}

                {cancelling && (
                    <ProgressIndicator show={true} message='Please wait...' />
                )}
            </button>

        </>
    }

    const handleReOpen = () => {
        if (!serviceRequest) return;

        setReopening(true);
        updateServiceRequest({
            ...serviceRequest,
            statusCode: ServiceRequestStatus.ReOpen,
        })
            .then(() => onReOpen(serviceRequest.id))
            .finally(() => setReopening(false));
    };

    const reOpenButton = () => {
        if (serviceRequest?.statusCode !== 365970002 || serviceRequest?.allowReOpen !== true)
            return <></>;
        return (
            <>
                <button
                    disabled={reopening}
                    type="button"
                    onClick={handleReOpen}
                    className="btn btn-sm btn-warning py-2 align-self-center"
                >
                    {!reopening && (
                        <>
                            <MUIIcon iconName={"RedoOutlined"} className="me-1" />
                            ReOpen the request
                        </>
                    )}
                    {reopening && <ProgressIndicator show={true} message="Please wait..." />}
                </button>
            </>
        );
    };

    const handleExtended = () => {
        if (!serviceRequest) return;

        setExtend(true);
        updateExtendServiceRequest(serviceRequest.id)
            .then((data) => {
                onExtended(serviceRequest.id, data)
            })
            .finally(() => setExtend(false));
    };

    const extendedButton = () => {
        if (gpOut !== true || gpIn !== false)
            return <></>;
        return (
            <>
                <button
                    disabled={extend}
                    type="button"
                    onClick={handleExtended}
                    className="btn btn-sm btn-warning py-2 align-self-center"
                >
                    {!extend && (
                        <>
                            <MUIIcon iconName={"ExtensionOutlined"} className="me-1" />
                            Extend the request
                        </>
                    )}
                    {extend && <ProgressIndicator show={true} message="Please wait..." />}
                </button>
            </>
        );
    };

    if (!serviceRequest)
        return <></>

    if (!serviceRequest.endTime && serviceRequest?.statusCode === ServiceRequestStatus.Submitted && isSendingForApproval)
        return <ProgressIndicator className="my-3" show={true} message='Sending approval request...' />

    return <div className='my-3'>
        <div className="d-flex gap-3">
            {countdownTimer(endTime)}
            <div className="flex-fill" />
            <div className="d-flex gap-3">
                {cancellationButton()}
                {reOpenButton()}
                {resendButtons(serviceRequest)}
                {extendedButton()}
            </div>
        </div>
        {showOutPopup && (
            <>
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0,0,0,0.4)",
                        zIndex: 9998
                    }}
                />
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 9999,
                        minWidth: "350px",
                        maxWidth: "500px"
                    }}
                    className="alert alert-danger shadow text-center"
                >
                    <div className="d-flex justify-content-between align-items-start">
                        <div style={{ fontSize: "14px", width: "100%" }}>
                            Gate pass cancellation is not permitted as your exit from the campus has already been recorded.
                        </div>
                        <button
                            type="button"
                            className="btn-close ms-2"
                            onClick={() => setShowOutPopup(false)}
                        />
                    </div>
                </div>
            </>
        )}
    </div>
}